import { createContext, useCallback, useContext, useEffect, useId, useLayoutEffect, useMemo, useRef, useState, useSyncExternalStore, type ReactNode } from "react";
import type { SurfaceFocus, SurfaceState } from "@ncp/assistant-sdk";
import type { PageLink } from "@ncp/page-host";
import { useMediaQuery } from "../lib/media";
// The ECONIC column's own state (WP-SHELL). A question asked from a screen opens the column (or its overlay or sheet) wherever it is folded.
// sidebar-state imports this module back; both use the other's exports only inside hooks, so the cycle is safe.
import { useSidebarStateOptional } from "../econic/shell/sidebar-state";

/**
 * Which conversation the dock holds: the space's assistant, an app's copilot pinned to its page, a study or policy's own thread, or (the
 * ECONIC Agent, builder §17.10) the department's follow-me thread that follows the person across screens (`surface`).
 */
export type DockKind = "space" | "page" | "matter" | "surface";

/**
 * How a question reaches the dock: sent at once, or left in the composer for the person to finish. `focus` is what "✦ Dive in" points at
 * (a widget, row, figure, document or answer block); it travels as `surface.focus` (builder §17.6).
 */
/** `directive` goes with the question to the agent (an accepted next step: `next <kind>`). */
export interface DockAskOptions { send: boolean; focus?: SurfaceFocus | null; directive?: string | null }

export interface DockPanel {
  kind: DockKind;
  title: string;
  subtitle?: string;
  /** The panel's accessible name when it differs from its title (an app's copilot is "Copilot" whatever its agent is called). */
  label?: string;
  /** What the panel is looking at, after the subtitle ("STD-2026-000009 · Tourism and the UAE economy"). */
  context?: string;
  icon?: ReactNode;
  /** Header buttons beside the close button (a new conversation, open in chats, suggest a change). */
  actions?: ReactNode;
  body: ReactNode;
  /** Runs when the dock opens on this panel (a work item starts its thread). */
  onOpen?: () => void;
  /** Takes a question from outside the panel (the Ask pill, a page's Ask button): sends it, or puts it in the composer. */
  ask?: (text: string, options: DockAskOptions) => void;
  /** Lets the panel go (a desk's conversation pinned to its page: unpin, so the department's follow-me thread returns). */
  release?: () => void;
}

const KINDS: readonly DockKind[] = ["space", "page", "matter", "surface"];
export const DOCK_MIN = 320;
export const DOCK_MAX = 720;
const DOCK_DEFAULT = 400;

interface Entry { panel: DockPanel | null; disabled: boolean }
interface Top { id: string; entry: Entry }
type AskHandler = (text: string, options: DockAskOptions) => void;

/** The panels screens registered, in mount order; the dock shows the most recent (a screen opened over another wins). */
class DockStore {
  private entries = new Map<string, Entry>();
  private listeners = new Set<() => void>();
  private top: Top | null = null;
  subscribe = (l: () => void) => { this.listeners.add(l); return () => this.listeners.delete(l); };
  current = () => this.top;
  set(id: string, entry: Entry) { this.entries.set(id, entry); this.emit(); }
  delete(id: string) { if (this.entries.delete(id)) this.emit(); }
  private emit() {
    const last = [...this.entries].at(-1);
    this.top = last ? { id: last[0], entry: last[1] } : null;
    for (const l of this.listeners) l();
  }
}

/**
 * What screens tell the ECONIC Agent about themselves (builder §17.7): the most recently mounted publisher wins (a drawer over a screen).
 * The sidebar reads it when a message is sent and shows its title and widget count in the context line. A publish only notifies when the
 * state really changed, so a screen may publish on every render.
 */
class SurfaceStore {
  private entries = new Map<string, SurfaceState>();
  private listeners = new Set<() => void>();
  private top: SurfaceState | null = null;
  private keyOf = new Map<string, string>();
  subscribe = (l: () => void) => { this.listeners.add(l); return () => this.listeners.delete(l); };
  current = () => this.top;
  set(id: string, state: SurfaceState | null) {
    const key = state ? JSON.stringify(state) : "";
    if ((this.keyOf.get(id) ?? "") === key && this.entries.has(id) === !!state) return;
    this.keyOf.set(id, key);
    if (state) this.entries.set(id, state);
    else this.entries.delete(id);
    this.emit();
  }
  delete(id: string) { this.keyOf.delete(id); if (this.entries.delete(id)) this.emit(); }
  private emit() {
    this.top = [...this.entries.values()].at(-1) ?? null;
    for (const l of this.listeners) l();
  }
}

/** The custom page in view that the follow-me thread works in (a desk, unless the person pinned the conversation to it). */
export interface SidebarPage {
  link: PageLink;
  /** The page's own context line ("Chapters · Q3 2026 draft") and starters for the tab in view. */
  context?: string | null;
  starters?: string[];
  /** Pins the conversation to this page (its own `page-copilot:<page>` thread, the old behaviour). */
  pin: () => void;
}

class PageStore {
  private entries = new Map<string, SidebarPage>();
  private listeners = new Set<() => void>();
  private top: SidebarPage | null = null;
  subscribe = (l: () => void) => { this.listeners.add(l); return () => this.listeners.delete(l); };
  current = () => this.top;
  set(id: string, page: SidebarPage) {
    const before = this.entries.get(id);
    this.entries.set(id, page);
    if (before && before.link === page.link && before.context === page.context && (before.starters ?? []).join("|") === (page.starters ?? []).join("|")) return;
    this.emit();
  }
  delete(id: string) { if (this.entries.delete(id)) this.emit(); }
  private emit() {
    this.top = [...this.entries.values()].at(-1) ?? null;
    for (const l of this.listeners) l();
  }
}

interface DockState {
  store: DockStore;
  surfaces: SurfaceStore;
  pages: PageStore;
  openBy: Record<DockKind, boolean>;
  setOpenFor: (kind: DockKind, open: boolean) => void;
  closeAll: () => void;
  sheet: boolean;
  setSheet: (open: boolean) => void;
  width: number;
  setWidth: (w: number, persist?: boolean) => void;
  wide: boolean;
  /** The space assistant's question handler, used when no screen registered a panel of its own (the launcher). */
  spaceAsk: { current: AskHandler | null };
  /** A question asked while no handler was mounted (the ECONIC column folded to its rail): delivered when one registers. */
  pendingAsk: { current: { text: string; options: DockAskOptions } | null };
}

const DockContext = createContext<DockState | null>(null);

const CLOSED: Record<DockKind, boolean> = Object.fromEntries(KINDS.map((k) => [k, false])) as Record<DockKind, boolean>;

/**
 * The dock's state. Under the ECONIC shell the agent column's own state (`pref:econic.chat`, the shell's `sidebar-state`) decides whether
 * it shows, follows the person across screens and devices, and drives `open` here; the os and legacy frames open it on request.
 */
export function DockProvider({ children }: { children: ReactNode }) {
  const store = useMemo(() => new DockStore(), []);
  const surfaces = useMemo(() => new SurfaceStore(), []);
  const pages = useMemo(() => new PageStore(), []);
  const [openBy, setOpenBy] = useState<Record<DockKind, boolean>>(CLOSED);
  const [sheet, setSheet] = useState(false);
  const [width, setWidthState] = useState(DOCK_DEFAULT);
  const wide = useMediaQuery("(min-width: 1024px)");
  const spaceAsk = useRef<AskHandler | null>(null);
  const pendingAsk = useRef<{ text: string; options: DockAskOptions } | null>(null);

  const setOpenFor = useCallback((kind: DockKind, open: boolean) => {
    setOpenBy((s) => (s[kind] === open ? s : { ...s, [kind]: open }));
  }, []);
  const closeAll = useCallback(() => {
    setOpenBy((s) => (KINDS.some((k) => s[k]) ? CLOSED : s));
    setSheet(false);
  }, []);
  // The legacy inline column's width (the ECONIC column's width is the shell's `pref:econic.chat`).
  const setWidth = useCallback((w: number) => {
    setWidthState(Math.round(Math.min(DOCK_MAX, Math.max(DOCK_MIN, w))));
  }, []);

  const value = useMemo<DockState>(() => ({ store, surfaces, pages, openBy, setOpenFor, closeAll, sheet, setSheet, width, setWidth, wide, spaceAsk, pendingAsk }), [store, surfaces, pages, openBy, setOpenFor, closeAll, sheet, width, setWidth, wide]);
  return <DockContext.Provider value={value}>{children}</DockContext.Provider>;
}

function useDockState(): DockState {
  const value = useContext(DockContext);
  if (!value) throw new Error("The AI dock is used outside the workspace shell.");
  return value;
}

/** Puts the screen's conversation in the dock while the screen is shown. */
export function useDockPanel(panel: DockPanel | null): void {
  const { store } = useDockState();
  const id = useId();
  useLayoutEffect(() => { store.set(id, { panel, disabled: false }); });
  useLayoutEffect(() => () => store.delete(id), [store, id]);
}

/** A screen that is itself a conversation (the chat page) has no dock. */
export function useNoDock(): void {
  const { store } = useDockState();
  const id = useId();
  useLayoutEffect(() => { store.set(id, { panel: null, disabled: true }); }, [store, id]);
  useLayoutEffect(() => () => store.delete(id), [store, id]);
}

/** The space assistant registers how it takes a question, so the Ask pill can reach it where no screen has a panel of its own. */
export function useSpaceAsk(handler: AskHandler | null): void {
  const { spaceAsk, pendingAsk } = useDockState();
  useLayoutEffect(() => {
    spaceAsk.current = handler;
  });
  // A question that arrived before this handler mounted (the agent was folded to its rail) is delivered once it is here.
  useEffect(() => {
    if (!handler || !pendingAsk.current) return;
    const { text, options } = pendingAsk.current;
    pendingAsk.current = null;
    handler(text, options);
  }, [handler, pendingAsk]);
  useLayoutEffect(() => () => { spaceAsk.current = null; }, [spaceAsk]);
}

/**
 * Opens or closes the dock from a screen without following its contents (a screen that registers a panel must not re-render on its own
 * registration).
 */
export function useDockControls() {
  const { openBy, setOpenFor, sheet, setSheet, wide } = useDockState();
  return useMemo(() => ({
    isOpen: (kind: DockKind) => (wide ? openBy[kind] : sheet),
    setOpen: (kind: DockKind, open: boolean) => (wide ? setOpenFor(kind, open) : setSheet(open)),
  }), [openBy, setOpenFor, sheet, setSheet, wide]);
}

export interface DockApi {
  available: boolean;
  open: boolean;
  kind: DockKind;
  panel: DockPanel | null;
  panelId: string | null;
  setOpen: (open: boolean) => void;
  toggle: () => void;
  /** Opens the dock and hands it a question: the screen's panel takes it, else the space's assistant. */
  ask: (text: string, options?: DockAskOptions) => void;
  wide: boolean;
  width: number;
  setWidth: (w: number, persist?: boolean) => void;
}

/** The dock as the shell shows it: the current screen's panel, open or closed per kind of screen; below `lg` a sheet. */
export function useDock(): DockApi {
  const state = useDockState();
  const econic = useSidebarStateOptional();
  const openColumn = econic?.open;
  const top = useSyncExternalStore(state.store.subscribe, state.store.current);
  const available = !top?.entry.disabled;
  const panel = top?.entry.panel ?? null;
  const kind: DockKind = panel?.kind ?? "space";
  const open = available && (state.wide ? state.openBy[kind] : state.sheet);
  const { setOpenFor, setSheet, wide, spaceAsk, pendingAsk } = state;
  const setOpen = useCallback((next: boolean) => {
    if (next) panel?.onOpen?.();
    if (wide) setOpenFor(kind, next);
    else setSheet(next);
  }, [panel, wide, setOpenFor, setSheet, kind]);
  const ask = useCallback((text: string, options: DockAskOptions = { send: true }) => {
    if (!available) return;
    setOpen(true);
    openColumn?.();
    const handler = panel ? panel.ask : spaceAsk.current;
    if (!text.trim()) return;
    if (handler) handler(text.trim(), options);
    else pendingAsk.current = { text: text.trim(), options };
  }, [available, setOpen, panel, spaceAsk, pendingAsk, openColumn]);
  return { available, open, kind, panel, panelId: top?.id ?? null, setOpen, toggle: () => setOpen(!open), ask, wide, width: state.width, setWidth: state.setWidth };
}

/**
 * Whether the dock is open, for layout (the window making room for it). Subscribes to the kind of panel only, never to the panel itself,
 * so a frame that re-renders its children does not re-render on every panel registration.
 */
export function useDockOpen(): boolean {
  const state = useDockState();
  const kind = useSyncExternalStore(state.store.subscribe, () => {
    const top = state.store.current();
    return top?.entry.disabled ? "none" : top?.entry.panel?.kind ?? "space";
  });
  if (kind === "none") return false;
  return state.wide ? state.openBy[kind] : state.sheet;
}

/**
 * Whether the current screen has a dock (and so the Ask pill), for layout: a primitive subscription like `useDockOpen`, so the frame does
 * not re-render on every panel registration (a panel registers on each render of its screen, which the frame renders).
 */
export function useDockAvailable(): boolean {
  const state = useDockState();
  return useSyncExternalStore(state.store.subscribe, () => !state.store.current()?.entry.disabled);
}

/** ⌘. (Ctrl+. elsewhere) opens and closes the dock. */
export function useDockShortcut(dock: DockApi): void {
  const { available, toggle } = dock;
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "." && available) {
        e.preventDefault();
        toggle();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [available, toggle]);
}

// ---------------------------------------------------------------- what the screen shows the ECONIC Agent (builder §17.7)

/**
 * Publishes what the screen shows the agent while the caller is mounted (the surface engine's `SurfaceStateProvider`, a module, a kept
 * screen). Pass `null` to publish nothing. The latest publisher wins; publishing the same state again is free.
 */
export function useSurfaceContext(state: SurfaceState | null): void {
  const ctx = useContext(DockContext);
  const id = useId();
  useLayoutEffect(() => { ctx?.surfaces.set(id, state); });
  useLayoutEffect(() => () => ctx?.surfaces.delete(id), [ctx, id]);
}

const NO_SURFACE = () => null;
const NO_SUBSCRIBE = () => () => undefined;

/** The surface the screen in view published (re-renders when it changes), or null when no screen published one. */
export function usePublishedSurface(): SurfaceState | null {
  const ctx = useContext(DockContext);
  return useSyncExternalStore(ctx?.surfaces.subscribe ?? NO_SUBSCRIBE, ctx?.surfaces.current ?? NO_SURFACE);
}

/** Reads the published surface at the moment of sending (no re-render). */
export function useReadPublishedSurface(): () => SurfaceState | null {
  const ctx = useContext(DockContext);
  return useCallback(() => ctx?.surfaces.current() ?? null, [ctx]);
}

/**
 * A desk registers itself with the follow-me thread (builder §17.4): each message then carries the page (`page: link.open()`) and the
 * page's client tools answer the agent's calls, so desk tools keep working in the department's conversation.
 */
export function useSidebarPage(page: SidebarPage | null): void {
  const ctx = useContext(DockContext);
  const id = useId();
  useLayoutEffect(() => {
    if (page) ctx?.pages.set(id, page);
    else ctx?.pages.delete(id);
  });
  useLayoutEffect(() => () => ctx?.pages.delete(id), [ctx, id]);
}

/** The desk the follow-me thread is working in, if any. */
export function useSidebarPageInView(): SidebarPage | null {
  const ctx = useContext(DockContext);
  return useSyncExternalStore(ctx?.pages.subscribe ?? NO_SUBSCRIBE, ctx?.pages.current ?? NO_SURFACE);
}
